import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import LazyLoad from "react-lazyload";
import ReactDOMServer from "react-dom/server";
import _ from "lodash";
import { AppRemixIcon, AppNoInfo } from "@/layout/AppComponents";
import BreadcrumbHandler from "@/layout/components/breadcrumb/BreadcrumbHandler";
import errorHandler from "@/layout/components/error/ErrorHandler";
import Translator from "@/layout/components/helpers/Translator";
import useIsMounted from "@/layout/components/hooks/useIsMounted";
import GGCLObligationContainer from "@/layout/screenSpecifics/Modules/GlobalLegalCompliance/Evaluation/GGCLObligationContainer";
import GlobalEvaluationSidebar from "@/layout/screenSpecifics/Modules/GlobalLegalCompliance/Evaluation/Sidebar/GlobalEvaluationSidebar";
import GCLPaginator from "@/layout/screenSpecifics/Modules/LegalCompliance/Evaluation/GCLPaginator";
import { axiosPath, path } from "@/routing/helpers/router";
import * as currentPageAction from "@/store/actions/currentPageAction";
import * as moduleAction from "@/store/actions/moduleAction";

export default function GlobalLegalComplianceEvaluationScreen() {
  const { moduleBridgeHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const poLevel1 = useSelector(
    (state) => state.currentPage?.obligationLevels?.level1,
  );
  const poLevel2 = useSelector(
    (state) => state.currentPage?.obligationLevels?.level2,
  );
  const poLevel3 = useSelector(
    (state) => state.currentPage?.obligationLevels?.level3,
  );
  const obligationPages = useSelector((state) => state.currentPage?.gcl?.obligations);
  const userMark = useSelector((state) => state.currentPage?.options?.userMark);
  const goToMark = useSelector((state) => state.currentPage?.goToMark);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null);
  const [obligations, setObligations] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(currentPageAction.change("obligationLevels", {}, true));
    dispatch(currentPageAction.change("checkedObligations", [], true));
  }, [filtersHex, dispatch]);

  // > LOAD LAYOUT
  const loadLayout = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_globalLegalCompliance_evaluation_loadLayout"),
          {
            moduleBridgeHex,
          },
        )
        .then((r) => {
          if (isMounted.current) setLayout(r.data);
          dispatch(currentPageAction.change("options", r.data.options));
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, dispatch, isMounted],
  );

  useEffect(() => {
    if (moduleBridgeHex) {
      loadLayout();
    }
  }, [moduleBridgeHex, page, loadLayout]);
  // < LOAD LAYOUT

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath(
            "api_r_user_module_globalLegalCompliance_evaluation_getPermissions",
          ),
          "permissions",
          {
            moduleBridgeHex,
          },
        ),
      );
    } catch {}
  }, [moduleBridgeHex, dispatch]);

  useEffect(() => {
    loadModulePermissions();
  }, [loadModulePermissions]);
  // < STORE CALLS

  // > LOAD PLANTOBLIGATIONS
  const loadObligations = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_globalLegalCompliance_evaluation_loadObligations",
          ),
          {
            moduleBridgeHex,
            filtersHex,
          },
        )
        .then((r) => {
          if (page > r?.data?.pagination?.last && page != 1) {
            navigate(
              path({
                name: "user_module_globalLegalCompliance_evaluation",
                params: {
                  moduleBridgeHex,
                  page: 1,
                  filtersHex: filtersHex,
                },
              }),
            );
            return;
          }

          setObligations(r.data);
          dispatch(
            currentPageAction.change("gcl", {
              obligations: r.data.obligationsIDs,
            }),
          );
          dispatch(currentPageAction.change("pagination", r.data.pagination));
          dispatch(
            currentPageAction.change("obligationsPage", r.data.obligationsIDs),
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, filtersHex, page, navigate, dispatch],
  );
  useEffect(() => {
    if (layout) {
      setLoading(true);
      loadObligations();
    }
  }, [layout, filtersHex, loadObligations]);
  // < LOAD PLANTOBLIGATIONS

  // > LOAD PLANTOBLIGATION LEVELS
  const loadPoLevel = async (level) => {
    await axios
      .post(
        axiosPath("api_r_user_module_globalLegalCompliance_evaluation_loadOLevel"),
        {
          moduleBridgeHex,
          oIDs: obligations.obligationsIDs[page - 1],
          level,
          filtersHex,
        },
      )
      .then(({ data }) => {
        if (level === 1) {
          dispatch(
            currentPageAction.change(
              "obligationLevels",
              { level1: !poLevel1 ? data : { ...poLevel1, ...data } },
              true,
            ),
          );
          setLoading(false);
          window.scrollTo(0, 0);
          loadPoLevel(2);
        }

        if (level === 2) {
          dispatch(
            currentPageAction.change("obligationLevels", {
              level2: !poLevel2 ? data : { ...poLevel2, ...data },
            }),
          );

          loadPoLevel(3);
        }

        if (level === 3) {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("obligationLevels", {
                level3: !poLevel3 ? data : { ...poLevel3, ...data },
              }),
            );
          }
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  };

  // Level 1
  useEffect(() => {
    if (obligations?.obligationsIDs) {
      if (obligations?.obligationsIDs.length > 0) loadPoLevel(1);
      else {
        setLoading(false);
        window.scrollTo(0, 0);
      }
    }
  }, [obligations, page]);
  // < LOAD PLANTOBLIGATIONS LEVELS

  // > GO TO MARKER
  useEffect(() => {
    if (
      _.filter(obligationPages, (p) => p.includes(userMark)).length > 0 &&
      goToMark
    ) {
      dispatch(currentPageAction.change("goToMark", false, true));
      const elementPosition = _.indexOf(obligationPages[page - 1], userMark);
      window.scrollTo({
        top: 168 * elementPosition + 70,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [userMark, goToMark, dispatch, obligationPages, page]);
  // < GO TO MARKER

  return (
    <>
      <GlobalEvaluationSidebar />
      <BreadcrumbHandler
        paths={
          layout
            ? [
                {
                  name: `${
                    layout.globalLegalCompliance.name
                  } - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )} (${
                    obligations?.pagination?.totalCount || "--"
                  } ${ReactDOMServer.renderToString(
                    <Translator file="module-intelligence" name="obligations" />,
                  )})`,
                },
              ]
            : [{ name: "..." }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className={`${isSidebarOpened && "sidebar-expanded"}`}>
          <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0 border-bottom">
            <section className="content mb-5">
              {filtersHex !== "0" && (
                <div className="alert alert-info pointer">
                  <span
                    onClick={() =>
                      navigate(
                        path({
                          name: "user_module_globalLegalCompliance_evaluation",
                          params: {
                            moduleBridgeHex,
                            page: 1,
                            filtersHex: "0",
                          },
                        }),
                      )
                    }
                  >
                    <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                    <Translator file="filter" name="filter.delete" />
                  </span>
                  <span
                    className="ml-4"
                    onClick={() =>
                      dispatch(
                        currentPageAction.change("auxSidebar", {
                          opened: "filters",
                        }),
                      )
                    }
                  >
                    <AppRemixIcon icon="equalizer" className="mr-2" />
                    <Translator file="module-legalCompliance" name="show.filters" />
                  </span>
                </div>
              )}
              {loading ? (
                <AppNoInfo minHeight={window.innerHeight - 60} />
              ) : layout && obligations?.obligationsIDs.length > 0 ? (
                obligations?.obligationsIDs &&
                obligations?.obligationsIDs.length > 0 &&
                obligations?.obligationsIDs[page - 1].map((oID, i) => (
                  <LazyLoad key={i} height={168}>
                    <GGCLObligationContainer type="aa" obligationID={oID} />
                  </LazyLoad>
                ))
              ) : (
                <div className="alert alert-info">
                  <Translator file="module-legalCompliance" name="no.obligations" />
                </div>
              )}
              {!loading && obligations?.obligationsIDs?.length > 0 && (
                <GCLPaginator
                  pagination={obligations.pagination}
                  route="user_module_globalLegalCompliance_evaluation"
                  page={page}
                  plantObligationPages={obligations?.obligationsIDs ?? []}
                  itemsPerPage={20}
                  className="mt-3"
                  params={{
                    moduleBridgeHex,
                    page,
                    filtersHex,
                  }}
                />
              )}
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
